import {ComplianceManagerApi} from "../../../compliance-manager";
import NonIdealState from "../../../../../NonIdealState";
import {RegisterVeriflytAccountButton} from "../../../../veriflyt-registerer/register-account-button";
import {useVeriflytRegistererContext, VeriflytRegistererContext} from "../../../../veriflyt-registerer/context";
import {Alert, Button, Descriptions, Divider, Form, Icon, message, Modal, Spin, Typography} from "antd";
import {FormComponentProps} from "antd/lib/form/Form";
import {useVeriflytIdState} from "../../../../veriflyt-id-input/veriflyt-id-input";
import VeriflytIdInput from "../../../../veriflyt-id-input";
import React from "react";

export type RegisterVeriflytAccountProps = { api: ComplianceManagerApi } & FormComponentProps;

function RegisterVeriflytAccount(props: RegisterVeriflytAccountProps) {
    const {api, form} = props;
    const veriflytResult = api.complianceResult?.veriflytProviderResult;

    const initialVeriflytID = veriflytResult?._veriflytResult?.personID;

    const registerContext = useVeriflytRegistererContext(api?.paxContext?.paxNode?.personID?._id);
    const veriflytIdState = useVeriflytIdState({
        veriflytPrefix: 'P',
        initialVeriflytID,
        form,
        fieldName: 'veriflytID',
        hasPermission: true
    });

    function handleFormSubmit(e: React.FormEvent) {
        e.preventDefault();
        form.validateFieldsAndScroll((err, values) => {
            if (!err){
                const veriflytIDValue = values[veriflytIdState.fieldName]?.inputValue;
                if (!veriflytIDValue) return;

                if (!veriflytIdState.account){
                    message.error('Veriflyt account not found. Cannot assign ID.');
                    return;
                }

                const paxName = `${api.paxContext.firstName} ${api.paxContext.lastName}`.trim().toUpperCase();
                const veriflytName = `${veriflytIdState.account.primaryName} ${veriflytIdState.account.secondaryName}`;
                const mismatchDetected = paxName.toUpperCase() !== veriflytName.toUpperCase();

                Modal.confirm({
                    type: 'warning',
                    title: 'Please Confirm Veriflyt ID Assignment',
                    content: <>
                        <p>Please confirm that the name for the passenger matches the account registered in VeriFlyt:</p>
                        {mismatchDetected ? (
                            <Alert
                                type="warning"
                                message="A name mismatch has been detected between the passenger and the Veriflyt account. Please ensure that you are assigning the correct Veriflyt ID."
                                style={{ marginBottom: 16 }}
                            />
                            ) : null
                        }
                        <Descriptions bordered layout="vertical" size="small">
                            <Descriptions.Item label="Passenger Name">{paxName}</Descriptions.Item>
                            <Descriptions.Item label="Veriflyt Name to Assign">
                                <Typography.Text type={mismatchDetected ? 'danger' : undefined}>{veriflytName}</Typography.Text>
                            </Descriptions.Item>
                        </Descriptions>
                        </>,
                    okText: 'Yes, Assign ID',
                    onOk: async () => {
                        try {
                            await registerContext.accountRegistrar.registerManual(veriflytIDValue);
                            message.success('Veriflyt ID assigned and account registered successfully.');
                        } catch (err) {
                            console.error(err);
                            message.error(`Failed to assign Veriflyt ID`);
                        }
                    }
                })
            }
        })
    }

    if (!veriflytResult?.reason || !veriflytResult.reason.includes('No VERIFLYT ID')) {
        return null;
    }

    return <div style={{marginTop: 48, marginBottom: 24}}>
        <VeriflytRegistererContext.Provider value={registerContext}>
            <NonIdealState
                icon="info-circle"
                title="Veriflyt Account Not Registered"
                description={
                    <>
                        <p>This passenger does not have a Veriflyt account registered. To perform compliance checks, please
                            register a Veriflyt account for this passenger.</p>
                        <Spin spinning={registerContext.accountRegistrar.isRegistering} indicator={<Icon type="loading" />}>
                            <RegisterVeriflytAccountButton type="primary" />
                            <Divider>or</Divider>
                            <Form
                                layout="inline"
                                onSubmit={handleFormSubmit}
                                style={{ textAlign: 'left', display: 'flex', justifyContent: 'center' }}
                            >
                                <Form.Item {...veriflytIdState.formItemProps}>
                                    {form.getFieldDecorator(veriflytIdState.fieldName, {
                                        rules: [
                                            ...veriflytIdState.formFieldDecoratorRules,
                                            {
                                                required: true,
                                                message: 'Please input or generate a Veriflyt ID'
                                            }
                                        ],
                                        initialValue: {
                                            inputValue: initialVeriflytID
                                        }
                                    })(
                                        <VeriflytIdInput
                                            veriflytPrefix={veriflytIdState.veriflytPrefix}
                                            initialVeriflytId={initialVeriflytID}
                                            hideGenBtn
                                            placeholder="Manually input ID"
                                            disabled={!veriflytIdState.hasPermission || registerContext.accountRegistrar.isRegistering || !registerContext.accountRegistrar.isValid}
                                        />
                                    )}
                                </Form.Item>
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        disabled={
                                            !veriflytIdState.hasPermission ||
                                            registerContext.accountRegistrar.isRegistering ||
                                            !registerContext.accountRegistrar.isValid ||
                                            !form.getFieldValue(veriflytIdState.fieldName)?.inputValue
                                        }
                                    >Assign ID</Button>
                                </Form.Item>
                            </Form>
                        </Spin>
                    </>
                }
            />
        </VeriflytRegistererContext.Provider>
    </div>
}

const WithForm = Form.create<RegisterVeriflytAccountProps>()(RegisterVeriflytAccount);

WithForm.displayName = 'WithForm(RegisterVeriflytAccount)';

export default WithForm;